import * as React from "react";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { EmailInput } from "./EmailInput";
import { FormError } from "./FormError";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, ArrowLeft } from "lucide-react";
import { Link } from "@tanstack/react-router";

/**
 * Forgot Password Form Component
 *
 * Form to request a password reset email.
 */
export function ForgotPasswordForm() {
  const { resetPassword, loading } = useAuth();

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [success, setSuccess] = useState(false);

  const validateEmail = (): boolean => {
    if (!email) {
      setEmailError("Email is required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email");
      return false;
    }
    setEmailError(undefined);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateEmail()) return;

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to send reset email. Please try again.";
      setError(message);
    }
  };

  if (success) {
    return (
      <div className="space-y-4">
        <Alert className="border-green-500/50 bg-green-500/10">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertDescription className="text-green-700 dark:text-green-400">
            Check your email for a password reset link. If you don't see it,
            check your spam folder.
          </AlertDescription>
        </Alert>

        <Link
          to="/login"
          className="inline-flex items-center text-sm text-primary hover:underline"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to login
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormError message={error} />

      <p className="text-sm text-muted-foreground">
        Enter your email address and we'll send you a link to reset your
        password.
      </p>

      <EmailInput
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setEmailError(undefined);
        }}
        error={emailError}
        disabled={loading}
      />

      <Button type="submit" className="w-full h-11 rounded-full" disabled={loading}>
        {loading ? (
          <>
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
            Sending...
          </>
        ) : (
          "Send reset link"
        )}
      </Button>

      <Link
        to="/login"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to login
      </Link>
    </form>
  );
}

export default ForgotPasswordForm;
